import type { RgbaImage } from '../image';
import type { PreparedMaps, PrintArea, Quad, ShadingSettings } from './model';
import { quadToSquare, insideUnit, quadBounds, applyH } from './homography';

export function lumaOf(img: RgbaImage): Float32Array {
  const n = img.width * img.height, out = new Float32Array(n), d = img.data;
  for (let i = 0, o = 0; i < n; i++, o += 4) out[i] = (0.3 * d[o] + 0.59 * d[o + 1] + 0.11 * d[o + 2]) / 255;
  return out;
}
/** Separable box blur with edge clamping; radius r (window 2r+1). */
export function boxBlurF32(src: Float32Array, w: number, h: number, r: number): Float32Array {
  if (r <= 0) return Float32Array.from(src);
  const tmp = new Float32Array(w * h), out = new Float32Array(w * h);
  const win = 2 * r + 1;
  for (let y = 0; y < h; y++) {
    const row = y * w; let acc = 0;
    for (let k = -r; k <= r; k++) acc += src[row + Math.min(w - 1, Math.max(0, k))];
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc / win;
      acc += src[row + Math.min(w - 1, x + r + 1)] - src[row + Math.max(0, x - r)];
    }
  }
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += tmp[Math.min(h - 1, Math.max(0, k)) * w + x];
    for (let y = 0; y < h; y++) {
      out[y * w + x] = acc / win;
      acc += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x];
    }
  }
  return out;
}
export function blur3(src: Float32Array, w: number, h: number, r: number): Float32Array { return boxBlurF32(boxBlurF32(boxBlurF32(src, w, h, r), w, h, r), w, h, r); }
export function sobel(src: Float32Array, w: number, h: number): { gx: Float32Array; gy: Float32Array } {
  const gx = new Float32Array(w * h), gy = new Float32Array(w * h);
  const at = (x: number, y: number) => src[Math.min(h - 1, Math.max(0, y)) * w + Math.min(w - 1, Math.max(0, x))];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const i = y * w + x;
    gx[i] = (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x - 1, y) - at(x - 1, y + 1)) / 8;
    gy[i] = (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1) - at(x - 1, y - 1) - 2 * at(x, y - 1) - at(x + 1, y - 1)) / 8;
  }
  return { gx, gy };
}
/** Every masked pixel inside `quads` — the union when there are several, each pixel visited once
 *  however many areas overlap it. The one-quad path is the original loop, untouched. */
function quadPixels(w: number, h: number, quads: Quad | Quad[], alpha: Uint8Array, fn: (i: number) => void): void {
  if (!Array.isArray(quads)) { quadPixels(w, h, [quads], alpha, fn); return; }
  if (quads.length === 1) {
    const Hi = quadToSquare(quads[0]), b = quadBounds(quads[0]);
    for (let y = Math.max(0, b.y0); y < Math.min(h, b.y1); y++) for (let x = Math.max(0, b.x0); x < Math.min(w, b.x1); x++) {
      const i = y * w + x; if (!alpha[i]) continue;
      const [u, v] = applyH(Hi, x + 0.5, y + 0.5); if (insideUnit(u, v)) fn(i);
    }
    return;
  }
  const His = quads.map(quadToSquare), bs = quads.map(quadBounds);
  let x0 = w, y0 = h, x1 = 0, y1 = 0;
  for (const b of bs) { if (b.x0 < x0) x0 = b.x0; if (b.y0 < y0) y0 = b.y0; if (b.x1 > x1) x1 = b.x1; if (b.y1 > y1) y1 = b.y1; }
  for (let y = Math.max(0, y0); y < Math.min(h, y1); y++) for (let x = Math.max(0, x0); x < Math.min(w, x1); x++) {
    const i = y * w + x; if (!alpha[i]) continue;
    for (let k = 0; k < His.length; k++) {
      const b = bs[k]; if (x < b.x0 || x >= b.x1 || y < b.y0 || y >= b.y1) continue;
      const [u, v] = applyH(His[k], x + 0.5, y + 0.5);
      if (insideUnit(u, v)) { fn(i); break; }
    }
  }
}
/** Int8 gradient normalised so the 99th-percentile magnitude inside the print area maps to 127. */
/** Floor for the gradient reference (luma per pixel of the fold-blurred image): a flat-lay photo whose
 *  99th-percentile gradient is below this is mostly sensor/JPEG noise, and normalising it up to the full
 *  ±127 turned that noise into a ±8 px "water ripple" over the art (measured on the shipped Gildan flats:
 *  p99 3.6e-4 … 8.1e-4 at the 12-px fold blur, against 5.2e-3 for the model shot's real folds). With
 *  the floor, flats displace ~0.7–1.6 px at the default wrinkle and real folds keep their full strength. */
export const GRADIENT_REF_MIN = 0.004;
export function quantizeGradient(gx: Float32Array, gy: Float32Array, w: number, h: number, alpha: Uint8Array, quad: Quad | Quad[], refMin = GRADIENT_REF_MIN): { gx: Int8Array; gy: Int8Array } {
  let count = 0;
  quadPixels(w, h, quad, alpha, () => { count++; });
  const mags = new Float32Array(count);
  let k = 0;
  quadPixels(w, h, quad, alpha, (i) => { mags[k++] = Math.hypot(gx[i], gy[i]); });
  mags.sort();   // typed-array sort is numeric by default (unlike Array.prototype.sort)
  const p99 = mags.length ? mags[Math.min(mags.length - 1, Math.floor(mags.length * 0.99))] : 0;
  const ref = Math.max(p99, refMin);
  const scale = ref > 1e-9 ? 127 / ref : 0;
  const qx = new Int8Array(w * h), qy = new Int8Array(w * h);
  for (let i = 0; i < w * h; i++) { qx[i] = Math.max(-127, Math.min(127, Math.round(gx[i] * scale))); qy[i] = Math.max(-127, Math.min(127, Math.round(gy[i] * scale))); }
  return { gx: qx, gy: qy };
}
export function residualOf(img: RgbaImage, r: number): Int8Array {
  const n = img.width * img.height, out = new Int8Array(n * 3);
  for (let c = 0; c < 3; c++) {
    const ch = new Float32Array(n); for (let i = 0; i < n; i++) ch[i] = img.data[i * 4 + c];
    const b = boxBlurF32(ch, img.width, img.height, r);
    for (let i = 0; i < n; i++) out[i * 3 + c] = Math.max(-127, Math.min(127, Math.round(ch[i] - b[i])));
  }
  return out;
}
export function meanInQuad(values: Float32Array, w: number, h: number, quad: Quad | Quad[], alpha: Uint8Array): number {
  let sum = 0, n = 0; quadPixels(w, h, quad, alpha, (i) => { sum += values[i]; n++; });
  return n ? sum / n : 0.5;
}
/** The drop shadow a swapped backdrop casts, all as fractions of the photo's WIDTH so a template
 *  looks the same at any resolution: blur radius 1 %, offset 0.5 % right and 1 % down, and the
 *  backdrop dimmed by at most 35 % where the shadow is solid. The GPU preview mirrors these
 *  numbers — change them here and in `FRAG_BASE` together. */
export const SHADOW_BLUR_FRAC = 0.01, SHADOW_DX_FRAC = 0.005, SHADOW_DY_FRAC = 0.01, SHADOW_STRENGTH = 0.35;
/**
 * The garment's shadow, 0–255: the feathered mask blurred by `blur3` at radius 1 % of the width and
 * then offset down-right, so it reads as the garment lifted a little off the new backdrop. Pixels
 * the offset has no source for (the first `dy` rows and `dx` columns) are 0 — the shadow simply does
 * not reach there. Only the backdrop is dimmed by it (`applyBackdrop`), never the garment.
 */
export function shadowMap(alpha: Uint8Array, w: number, h: number): Uint8Array {
  const n = w * h, src = new Float32Array(n);
  for (let i = 0; i < n; i++) src[i] = alpha[i] / 255;
  const blurred = blur3(src, w, h, Math.max(1, Math.round(SHADOW_BLUR_FRAC * w)));
  const dx = Math.round(SHADOW_DX_FRAC * w), dy = Math.round(SHADOW_DY_FRAC * w);
  const out = new Uint8Array(n);
  for (let y = 0; y < h; y++) {
    const sy = y - dy;
    if (sy < 0 || sy >= h) continue;
    // dx and dy are both >= 0 (they are fractions of a positive width), so a destination x >= dx
    // always has a source column and only the leading band needs skipping.
    for (let x = dx; x < w; x++) out[y * w + x] = Math.min(255, Math.max(0, Math.round(255 * blurred[sy * w + x - dx])));
  }
  return out;
}
export function autoBlurPx(width: number): number { return Math.max(1, Math.round(0.003 * width)); }
/** The fold blur is wider than the texture blur: folds are tens of pixels across, JPEG blocks are 8 px,
 *  so a ~12-px (at 1000 px) blur keeps the folds' gradients and removes the block edges that otherwise
 *  became the displacement field. */
export function autoFoldBlurPx(width: number): number { return Math.max(2, Math.round(0.012 * width)); }
/** `printAreas` may be one area or the template's whole list: the gradient reference and `lmid`
 *  are then normalised over the UNION of the quads, so a print in a second area is lit and
 *  displaced on the same scale as the primary one rather than against its own local statistics. */
export function prepareMaps(photo: RgbaImage, alpha: Uint8Array, printAreas: PrintArea | PrintArea[], shading: ShadingSettings): PreparedMaps {
  const w = photo.width, h = photo.height;
  const quads = (Array.isArray(printAreas) ? printAreas : [printAreas]).map((a) => a.quad);
  const r = shading.blurPx === 'auto' ? autoBlurPx(w) : shading.blurPx;
  const fr = shading.foldBlurPx === undefined || shading.foldBlurPx === 'auto' ? autoFoldBlurPx(w) : shading.foldBlurPx;
  const L = lumaOf(photo);
  // shading (shadow/highlight) and the displacement both read the fold-scale luma; the texture
  // residual keeps the fine radius so the weave survives
  const lumaBlur = blur3(L, w, h, fr);
  const g = sobel(lumaBlur, w, h);
  const q = quantizeGradient(g.gx, g.gy, w, h, alpha, quads);
  const lmid = shading.lmid === 'auto' ? Math.min(0.95, Math.max(0.05, meanInQuad(lumaBlur, w, h, quads, alpha))) : shading.lmid;
  return { width: w, height: h, mask: alpha, lumaBlur, gx: q.gx, gy: q.gy, residual: residualOf(photo, r), lmid };
}
